import { useMemo, useState } from 'react';

import { browserTimeZone } from '~/components/Automations/utils/automationSchedule';
import { useWorkspaceAgentExpandedAssetView } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentExpandedAssetView';
import { buildWorkspaceAgentVisualContext } from '~/components/v2/components/workspace/workspace-agent-panel/utils/buildWorkspaceAgentVisualContext';
import { useViewsSelectAllStore } from '~/components/v2/store/features/views/useViewsSelectAllStore';
import { useClipsFilters } from '~/hooks/filters/useClipsFilters';
import { useAppliedSortField } from '~/hooks/search/useAppliedSortField';
import { useIsFlattenedView } from '~/hooks/search/useIsFlattenedView';
import { useSearchParams } from '~/hooks/search/useSearchParams';
import { useURLBoardIdSelector } from '~/hooks/useURLBoardIdSelector';
import { useURLLibraryIdSelector } from '~/hooks/useURLLibraryIdSelector';
import { useFiltersContext } from '~/providers/FiltersProvider';

export const usePrivateWorkspaceAgentVisualContext = () => {
  const boardId = useURLBoardIdSelector();
  const libraryId = useURLLibraryIdSelector();
  const { assetViewOpen } = useWorkspaceAgentExpandedAssetView();
  const { filtersParams } = useClipsFilters();
  const { filters: userFilters } = useFiltersContext();
  const { search } = useSearchParams();
  const { isFlattenedView } = useIsFlattenedView();
  const selectAll = useViewsSelectAllStore((state) => state.selectAll);
  const includeNestedItems = useViewsSelectAllStore((state) => state.includeNestedItems);
  const { appliedSortField } = useAppliedSortField();
  const sortName = appliedSortField?.name;
  const sortDirection = appliedSortField?.direction;
  const [timeZone] = useState(browserTimeZone);

  const visualContext = useMemo(
    () =>
      buildWorkspaceAgentVisualContext({
        assetView: assetViewOpen,
        boardId: boardId || undefined,
        clipsFilters: { ...filtersParams },
        hasUserFilters: Object.keys(userFilters).length > 0,
        includeNestedItems,
        isFlattenedView,
        libraryId: libraryId || undefined,
        query: search,
        selectAll,
        sort: sortName && sortDirection ? { direction: sortDirection, name: sortName } : undefined,
        timeZone,
      }),
    [
      assetViewOpen,
      boardId,
      filtersParams,
      includeNestedItems,
      isFlattenedView,
      libraryId,
      search,
      selectAll,
      sortDirection,
      sortName,
      timeZone,
      userFilters,
    ],
  );

  return { visualContext };
};
